import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Bookmark, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

// Category colors
const CATEGORY_COLORS = {
  body: "#7A9E7E",
  emotions: "#7D98A1",
  boundaries: "#A87C6D",
  clarity: "#B8A088",
  ritual: "#9B8AA6",
  joy: "#D4A574",
};

const CATEGORY_NAMES = {
  body: "Body & Nervous System",
  emotions: "Emotions & Inner Climate",
  boundaries: "Energy & Boundaries",
  clarity: "Clarity & Direction",
  ritual: "Ritual & Inner Wisdom",
  joy: "Joy, Gratitude & Self-Love",
};

export default function SavedPrompts({ auth }) {
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedPrompts();
  }, []);

  const fetchSavedPrompts = async () => {
    try {
      const response = await fetch(`${API}/prompts/saved`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSavedPrompts(data.saved_prompts || []);
      }
    } catch (error) {
      console.error('Error fetching saved prompts:', error);
      toast.error("Failed to load saved prompts");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (promptId) => {
    try {
      const response = await fetch(`${API}/prompts/save/${promptId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setSavedPrompts(prev => prev.filter(p => p.prompt_id !== promptId));
        toast("Prompt removed", { duration: 2000 });
      }
    } catch (error) {
      console.error('Error removing prompt:', error);
      toast.error("Failed to remove prompt");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E6E2DD]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          
          <h1 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Saved Prompts
          </h1>
          
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-8 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#A87C6D] animate-spin" />
          </div>
        ) : savedPrompts.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-[#E6E2DD] flex items-center justify-center mx-auto mb-4">
              <Bookmark className="w-8 h-8 text-[#A8A4A0]" />
            </div>
            <h2 
              className="text-xl font-light text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              No saved prompts yet
            </h2>
            <p className="text-[#A8A4A0] mb-6">
              During check-ins, tap the bookmark icon to save prompts that resonate with you.
            </p>
            <Link to="/check-in">
              <Button className="rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Start a Check-in
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#A8A4A0] mb-6">
              {savedPrompts.length} saved prompt{savedPrompts.length !== 1 ? 's' : ''}
            </p>
            
            {savedPrompts.map(prompt => (
              <div 
                key={prompt.prompt_id}
                className="bg-white rounded-2xl border border-[#E6E2DD] p-6 group"
                data-testid={`saved-prompt-${prompt.prompt_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p 
                      className="text-lg font-light text-[#2D2A26] mb-3"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      "{prompt.text}"
                    </p>
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs px-3 py-1 rounded-full"
                        style={{ 
                          backgroundColor: `${CATEGORY_COLORS[prompt.category]}15`,
                          color: CATEGORY_COLORS[prompt.category]
                        }}
                      >
                        {CATEGORY_NAMES[prompt.category] || prompt.category}
                      </span>
                      <span className="text-xs text-[#A8A4A0]">
                        Saved {new Date(prompt.saved_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(prompt.prompt_id)}
                    className="p-2 text-[#E6E2DD] hover:text-[#C8553D] transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove from saved"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
